import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import api from '../lib/api';

export const Route = createFileRoute('/checkout/success')({
  component: CheckoutSuccessPage,
  validateSearch: (search: Record<string, unknown>): { session_id?: string } => {
    return {
      session_id: search.session_id as string | undefined,
    };
  },
});

function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const session_id = search.session_id;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['checkout-verification', session_id],
    queryFn: async () => {
      if (!session_id) {
        throw new Error('Session ID não fornecido');
      }
      const response = await api.get(`/subscriptions/verify/${session_id}`);
      return response.data;
    },
    enabled: !!session_id,
    retry: false, // Não fazer retry automático, vamos fazer polling manual
    refetchInterval: (query) => {
      // Se está processando, fazer polling a cada 3 segundos
      const data = query.state.data;
      if (data && !data.success && data.data?.processing) {
        return 3000;
      }
      return false;
    },
  });

  useEffect(() => {
    // O endpoint retorna subscription.workspaceId
    if (data?.success && data?.data?.subscription?.workspaceId) {
      const workspaceId = data.data.subscription.workspaceId;
      // Redirect to workspace after 2 seconds
      const timer = setTimeout(() => {
        navigate({
          to: '/w/$workspaceId/clients',
          params: { workspaceId },
        });
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [data, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Verificando pagamento...
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Aguarde enquanto confirmamos sua assinatura
          </p>
        </div>
      </div>
    );
  }

  if (!session_id) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-3 inline-block mb-4">
            <svg
              className="w-16 h-16 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Sessão Inválida
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Não foi possível identificar a sessão de pagamento. Por favor, verifique seu workspace ou entre em contato com o suporte.
          </p>
          <button
            onClick={() => navigate({ to: '/workspaces' })}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Ir para Workspaces
          </button>
        </div>
      </div>
    );
  }

  // Se está processando, mostrar tela de processamento
  if (data && !data.success && data.data?.processing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Processando Pagamento
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {data.data?.message || 'Seu pagamento foi confirmado e está sendo processado. Isso pode levar alguns momentos.'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Aguarde enquanto finalizamos sua assinatura...
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
          >
            Verificar novamente
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-3 inline-block mb-4">
            <svg
              className="w-16 h-16 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Erro ao Verificar Pagamento
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Não foi possível verificar o status do pagamento. Por favor, verifique seu workspace ou entre em contato com o suporte.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => refetch()}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Tentar Novamente
            </button>
            <button
              onClick={() => navigate({ to: '/workspaces' })}
              className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Ir para Workspaces
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="max-w-md text-center">
        <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-3 inline-block mb-4">
          <svg
            className="w-16 h-16 text-green-600 dark:text-green-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Pagamento Confirmado!
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Sua assinatura foi criada com sucesso. Redirecionando para seu workspace...
        </p>
        <div className="animate-pulse text-indigo-600 dark:text-indigo-400">
          Aguarde...
        </div>
      </div>
    </div>
  );
}
